/**
 * Helpers for booking discount codes on booking GET / edit / payment.
 *
 * Actual GET shape uses booking.booking_discount_usages[] (also on group_bookings).
 * Snapshot may live at:
 * - booking_request_meta.booking_discounts (nested), or
 * - booking_request_meta itself (flat: discountCode, discountedLineItems, ...)
 * Older / reschedule props may still pass a flat booking_discounts object.
 *
 * Note: discountedLineItems only lists discounted services — non-discounted
 * services in the same group are omitted. Prefer booking line totals for subtotal.
 */

function collectDiscountUsages(bookingDetailsOrEvent) {
    const fromRoot = bookingDetailsOrEvent?.booking_discount_usages;
    const fromBooking = bookingDetailsOrEvent?.booking?.booking_discount_usages;
    const fromGroups = (bookingDetailsOrEvent?.group_bookings || []).flatMap(
        (groupBooking) => groupBooking?.booking_discount_usages || [],
    );

    const usages = [
        ...(Array.isArray(fromRoot) ? fromRoot : []),
        ...(Array.isArray(fromBooking) ? fromBooking : []),
        ...fromGroups,
    ];

    const seen = new Set();
    return usages.filter((usage) => {
        const key = usage?.id || `${usage?.booking_id}-${usage?.service_id}-${usage?.discount_code}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    });
}

function extractMetaDiscountSnapshot(usage) {
    const meta = usage?.booking_request_meta;
    if (!meta) return null;
    if (meta.booking_discounts?.discountCode) {
        return meta.booking_discounts;
    }
    if (meta.discountCode) {
        return meta;
    }
    return null;
}

function normalizeFromUsages(usages) {
    if (!usages?.length) return null;

    // Prefer request snapshot when present (may omit non-discounted services in lines)
    const metaShape = usages.map(extractMetaDiscountSnapshot).find((discounts) => discounts?.discountCode);
    if (metaShape?.discountCode) {
        return metaShape;
    }

    const discountCode = usages.find((usage) => usage?.discount_code)?.discount_code || '';
    if (!discountCode) return null;

    const discountedLineItems = usages.map((usage) => ({
        itemId: Number(usage.service_id),
        itemType: 'ONLINE_BOOKING',
        itemPrice: Number(usage.service_price) || 0,
        discountAmount: Number(usage.discount_amount) || 0,
        finalAmount: Number(usage.final_amount) || 0,
    }));

    return {
        discountCode,
        discountedLineItems,
        totalDiscountAmount: discountedLineItems.reduce(
            (sum, line) => sum + (Number(line.discountAmount) || 0),
            0,
        ),
        // Only discounted services — callers must not use this as full-group total
        totalBookingAmount: discountedLineItems.reduce(
            (sum, line) => sum + (Number(line.finalAmount) || 0),
            0,
        ),
    };
}

export function getBookingDiscounts(bookingDetailsOrEvent) {
    if (!bookingDetailsOrEvent) return null;

    const direct =
        bookingDetailsOrEvent.booking_discounts ||
        bookingDetailsOrEvent.booking?.booking_discounts ||
        null;
    if (direct?.discountCode) {
        return direct;
    }

    return normalizeFromUsages(collectDiscountUsages(bookingDetailsOrEvent));
}

export function hasAppliedBookingDiscount(bookingDetailsOrEvent) {
    const discounts = getBookingDiscounts(bookingDetailsOrEvent);
    return Boolean(discounts?.discountCode);
}

export function getBookingDiscountCode(bookingDetailsOrEvent) {
    return getBookingDiscounts(bookingDetailsOrEvent)?.discountCode || '';
}

export function getBookingDiscountTotal(bookingDetailsOrEvent) {
    const discounts = getBookingDiscounts(bookingDetailsOrEvent);
    if (!discounts) return 0;
    if (discounts.totalDiscountAmount != null) {
        return Number(discounts.totalDiscountAmount) || 0;
    }
    const lines = discounts.discountedLineItems || [];
    return lines.reduce((sum, line) => sum + (Number(line.discountAmount) || 0), 0);
}

/** Original line amount from booking columns. */
export function getBookingLineSubTotal(bookingItem) {
    if (!bookingItem) return 0;
    return Number(bookingItem?.sub_total) || Number(bookingItem?.booking_details?.sub_total) || 0;
}

/** Net line amount (total_amount after discount) from booking columns. */
export function getBookingLinePaidAmount(bookingItem) {
    if (!bookingItem) return 0;
    return Number(bookingItem?.total_amount) || Number(bookingItem?.booking_details?.price) || 0;
}

/** Discount on a single booking line from columns. */
export function getBookingLineDiscount(bookingItem, bookingDetailsOrEvent = null) {
    if (!bookingItem) return 0;
    const fromColumns =
        Number(bookingItem?.total_discount) || Number(bookingItem?.booking_details?.total_discount) || 0;
    if (fromColumns > 0) return fromColumns;

    if (bookingDetailsOrEvent) {
        const serviceId = bookingItem?.service_id ?? bookingItem?.booking_details?.service_id;
        const line = findDiscountedLineForService(getBookingDiscounts(bookingDetailsOrEvent), serviceId);
        if (line?.discountAmount != null) {
            return Number(line.discountAmount) || 0;
        }
    }
    return 0;
}

export function sumBookingLineAmounts(bookingItems, getAmount) {
    if (!Array.isArray(bookingItems) || bookingItems.length === 0) return 0;
    return bookingItems.reduce((acc, item) => acc + (Number(getAmount(item)) || 0), 0);
}

/**
 * Group / single booking money breakdown for display.
 * Subtotal = sum of original line prices (sub_total).
 * netTotal = sum of net line prices (total_amount / price).
 * Discount = subtotal − netTotal (usage snapshots may only list one discounted line).
 */
/** List-row amounts from booking API object (customer bookings, history). */
export function getListBookingDisplayAmounts(book) {
    if (!book) {
        return { subtotal: 0, netTotal: 0, discount: 0 };
    }

    if (book?.inspection_data?.total_payment) {
        const netTotal = parseFloat(book.inspection_data.total_payment ?? 0);
        return { subtotal: netTotal, netTotal, discount: 0 };
    }

    const subtotal = getBookingLineSubTotal(book);
    const netTotal = getBookingLinePaidAmount(book);
    const discount = Math.max(0, subtotal - netTotal);

    return { subtotal, netTotal, discount };
}

export function getBookingGroupAmounts(bookingItems, singleBooking = null) {
    const items =
        Array.isArray(bookingItems) && bookingItems.length > 0
            ? bookingItems
            : singleBooking
              ? [singleBooking]
              : [];

    const subtotal = sumBookingLineAmounts(items, getBookingLineSubTotal);
    const netTotal = sumBookingLineAmounts(items, getBookingLinePaidAmount);
    const discount = Math.max(0, subtotal - netTotal);

    return { subtotal, netTotal, discount };
}

/** True when original subtotal > 0 but net service amount is 0 (e.g. mark as free). */
export function isBookingFullyDiscounted(subtotal, netTotal) {
    return Number(subtotal) > 0 && Number(netTotal) === 0;
}

/** Mark-as-free inspection service: full sub_total with zero net and no promo discount code. */
export function isBookingMarkAsFreeService(bookingItem, bookingDetailsOrEvent, isInspectionEnabled) {
    if (!isInspectionEnabled || !bookingItem) return false;
    if (hasAppliedBookingDiscount(bookingDetailsOrEvent)) return false;
    return isBookingFullyDiscounted(
        getBookingLineSubTotal(bookingItem),
        getBookingLinePaidAmount(bookingItem),
    );
}

/** POS item discount fields for mark-as-free (100% off original service price). */
export function buildMarkAsFreePosDiscountFields(fullPrice, discountName = 'Mark as free') {
    const itemPrice = Number(fullPrice) || 0;
    if (itemPrice <= 0) {
        return {
            discountAmount: 0,
            discountPercentage: 0,
            discountType: 'VARIABLE_PERCENTAGE',
            discounts: [],
            originalPrice: itemPrice,
            finalAmount: 0,
        };
    }

    return {
        discountAmount: itemPrice,
        discountPercentage: 100,
        discountType: 'VARIABLE_PERCENTAGE',
        originalPrice: itemPrice,
        finalAmount: 0,
        discounts: [
            {
                discountId: null,
                couponId: null,
                discountName,
                discountAmount: itemPrice,
                amountType: 'VARIABLE_PERCENTAGE',
                amount: 100,
            },
        ],
    };
}

export function findDiscountedLineForService(bookingDiscounts, serviceId) {
    if (!bookingDiscounts || serviceId == null) return null;
    const lines = bookingDiscounts.discountedLineItems || [];
    return (
        lines.find(
            (line) =>
                Number(line.itemId) === Number(serviceId) &&
                (line.itemType === 'ONLINE_BOOKING' || !line.itemType),
        ) || null
    );
}

export function formatDiscountPercentLabel(bookingDiscounts) {
    if (!bookingDiscounts) return null;
    if (bookingDiscounts.discountType === 'PERCENTAGE' && bookingDiscounts.discountValue != null) {
        return `-${Number(bookingDiscounts.discountValue)}%`;
    }
    return null;
}

/** POS cart discount fields derived from a booking discount line. */
export function buildPosDiscountFields(bookingDiscounts, serviceId) {
    const line = findDiscountedLineForService(bookingDiscounts, serviceId);
    if (!line || !(Number(line.discountAmount) > 0)) {
        return {
            discountAmount: 0,
            discountPercentage: 0,
            discountType: 'VARIABLE_PERCENTAGE',
            discounts: [],
            originalPrice: null,
            finalAmount: null,
        };
    }

    const discountAmount = Number(line.discountAmount) || 0;
    const itemPrice = Number(line.itemPrice) || 0;
    const code = bookingDiscounts.discountCode || '';

    // The booking already distributed the discount across its lines by weight, so the POS
    // line must carry that exact amount. VARIABLE_PERCENTAGE would make the cart recompute
    // it from the percentage and drift away from the booking's per-line split.
    return {
        discountAmount,
        discountPercentage: 0,
        discountType: 'VARIABLE_AMOUNT',
        originalPrice: itemPrice,
        finalAmount: Number(line.finalAmount) ?? Math.max(0, itemPrice - discountAmount),
        discounts: [
            {
                discountId: null, // Booking discount IDs are not POS discount module IDs
                couponId: null,
                discountName: code ? `Booking Discount (code: ${code})` : 'Booking Discount',
                discountAmount,
                amountType: 'VARIABLE_AMOUNT',
                amount: discountAmount,
                discountSource: 'BOOKING',
            },
        ],
    };
}
